import React, {useRef} from 'react';
import {useWindowDimensions} from 'react-native';
import {debounce} from 'lodash';

// Define a set of base font sizes
const baseFontSizes = {
  title: 24,
  subtitle: 18,
  heading1: 16,
  heading2: 14,
  paragraph: 12,
  small: 8,
};

export type FontSizeKey = keyof typeof baseFontSizes;
const breakpoints = {
  phone: 600,
  tablet: 900,
  largeTablet: 1200,
  desktop: 1800,
};

const getFontSizeForDimension = (
  width: number,
  height: number,
  baseSize: number,
) => {
  const maxDimension = Math.max(width, height);
  if (maxDimension < breakpoints.phone) {
    return baseSize * 0.8;
  }
  if (maxDimension < breakpoints.tablet) {
    return baseSize;
  }
  if (maxDimension < breakpoints.largeTablet) {
    return baseSize * 1.2;
  }
  if (maxDimension < breakpoints.desktop) {
    return baseSize * 1.4;
  }
  return baseSize * 1.6;
};

export const useDebounce = (fn: (...args: any[]) => any) => {
  return useRef(debounce(fn, 150)).current;
}

const useDebouncedFontSize = (key: FontSizeKey) => {
  const {width, height} = useWindowDimensions();
  const baseSize = baseFontSizes[key];
  const _debounce = useDebounce((w, h) => [w, h]);

  const [debouncedWidth, debouncedHeight] = React.useMemo(() => {
    const debounced = _debounce(width, height);
    return debounced || [width, height];
  }, [width, height, _debounce]);

  return React.useMemo(() => {
    return getFontSizeForDimension(debouncedWidth, debouncedHeight, baseSize);
  }, [debouncedWidth, debouncedHeight, baseSize]);
};

export const useFontSizes: () => {
  [key in FontSizeKey]: number;
} = () => ({
  title: useDebouncedFontSize('title'),
  subtitle: useDebouncedFontSize('subtitle'),
  heading1: useDebouncedFontSize('heading1'),
  heading2: useDebouncedFontSize('heading2'),
  paragraph: useDebouncedFontSize('paragraph'),
  small: useDebouncedFontSize('small'),
});

const useKeyFreeze = (obj: Record<string, any>) =>
  Object.freeze(
    Object.keys(obj).reduce((acc: Record<string, FontSizeKey>, key: string) => {
      acc[key] = key as FontSizeKey;
      return acc;
    }, {} as {[key in FontSizeKey]: FontSizeKey}),
  );

export default Object.assign(
  useDebouncedFontSize,
  useKeyFreeze(baseFontSizes),
  useKeyFreeze(breakpoints),
);
